import React, { useCallback, useMemo, useState } from 'react';
import { ContextMenu, ContextMenuBuilder, type RepeatRowsPayload } from '@/components/custom/erp/input-table-v2';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { SelectedCellInfo, useDetailPaste } from '../../../hooks';
import type { AccountConstraint } from '@/types/schemas';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<BanHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<BanHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onRepeatForRows: (payload: RepeatRowsPayload<BanHangRecord>) => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<BanHangRecord[]>; resultField?: string }>
  ) => void;
  constraints?: AccountConstraint[];
}

const DETAIL_ACTION_ORDER = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'] as const;
const noop = () => {};
const detail = (row: BanHangSearchParams) => constraintValues(row);

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onRepeatForRows,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  constraints = []
}) => {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: BanHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const { fetchDetail } = useCRUD({
    endpoint: QUERY_KEYS.VAT_TU,
    initialFetch: false
  });
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });
  const columns = useMemo(
    () => getDetailTableColumns(formMode, onCellValueChange, fetchDetail, cellError) as BanHangRecord[],
    [cellError, fetchDetail, formMode, onCellValueChange]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => {
              console.log('Copy data per column:', text);
            }
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );
  const handleRowId = useCallback((row: BanHangRecord) => row?.uuid || '', []);
  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: DataTableRenderCellParams) => {
      onCellClick?.(params);

      setContextMenu({
        position: { x: event.clientX, y: event.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <>
      <ErpInputTableV2
        rows={rows}
        autoHeight
        showIndexColumn
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={onRowClick as BanHangRecord}
        onCellClick={onCellClick as BanHangRecord}
        onCellContextMenu={handleCellContextMenu as BanHangRecord}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={handleRowId}
        pasteTarget={selectedCell}
        actionsDisabled={formMode === 'view'}
        actionOrder={DETAIL_ACTION_ORDER as BanHangRecord}
        actions={tableActions}
      />
      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() => {
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          });
        }}
      />
    </>
  );
};

export default DetailTab;
